import React, { ReactNode } from 'react';

export const L10nRevenueIntelligence = /** @type {const} */ {
  company: 'Company',
  dealPipeline: 'Deal Pipeline',
  viewTimeline: 'View timeline',
  noResultsFound: (objectName: string) =>
    `No ${objectName} found for the applied filters`,
  deals: {
    deals: 'Deals',
    viewDealComments: 'View Deal Comments',
    riskScore: {
      keyAssessmentFactors: 'Key assessment factors',
      assessmentFactorsNotAvailable:
        'Assessment factors contributing to this risk score are not currently available. Please check back later',
      assessmentFactorsNotAvailableError: 'Assessment factors not available.',
      noAssessmentFactorsAvailable: 'No assessment factors available.',
      basedOn: (count: number) => `Based on ${count} key risk indicators`,
      scoreAssessment: 'Score Assessment',
      dealRisks: 'Deal Risks',
      forecastRisks: 'Forecast Risks',
      positive: (count: number) => (count === 1 ? 'Positive' : 'Positives'),
      negative: (count: number) => (count === 1 ? 'Negative' : 'Negatives')
    },
    alerts: {
      title: 'Deal Health Alerts'
    },
    title: 'Deals',
    objectsName: 'deals'
  },
  quickView: {
    properties: {
      groupHelpTitle: 'How groups work?',
      editBanner:
        'Edits made to this group will be applied for the entire organization.',
      createBanner:
        'Once created, this group will be visible to the entire organization.',
      moveGroupUp: 'Move group up',
      moveGroupDown: 'Move group down'
    }
  },
  dashboard: {
    columns: {
      riskScore: 'Risk Score',
      lowRisk: 'Low Risk',
      mediumRisk: 'Medium Risk',
      highRisk: 'High Risk',
      viewConfig: 'View Config'
    },
    groups: {
      deal: 'Deal',
      company: 'Company'
    }
  },
  aiMethodology: {
    sidebar: 'AI Methodologies',
    tracker: 'Qualification Score',
    qualificationScore: {
      description:
        'AI evaluates each methodology topic and aggregates them into a deal qualification score.',
      weakFit: 'Weak fit',
      moderateFit: 'Moderate fit',
      strongFit: 'Strong fit'
    },
    methodologyTracker: 'Methodology Tracker',
    playbooksInDealsDashboard: 'AI Methodology in Deals Dashboard',
    illustrationTitle:
      'The AI Methodology will be added as a column in the Deals Dashboard for each deal pipeline',
    illustrationDescription:
      'When enabled, Avoma AI will automatically assess these segments across all meetings and calls associated with a deal in the Revenue Intelligence tab.',
    dealPipelineTooltip:
      'Information about what pipeline this methodology will be associated with.',
    enableConfirmation: {
      withoutPipelines:
        'Once enabled, this AI methodology will apply to all deals.\n\nIt may take up to 24 hours for the extracted insights to be reflected in the dashboard.',
      withPipelines:
        'Once enabled, this AI methodology will apply to the selected pipeline(s).\n\nIt may take up to 24 hours for the extracted insights to be reflected in the dashboard.'
    },
    disableConfirmation: `Disabling this methodology will:\n\n• Stop deal scoring for deals in the pipeline
• Revert to using purpose-based note templates
• Disable call scoring with conversation scorecard templates
• Turn off live conversation mentoring\n\nYou can re-enable this methodology at any time.`,
    methodologyInfo: 'Methodology Info',
    methodologySettings: 'Methodology Settings',
    segmentInformation: 'Topic Information',
    mapSegmentsToCrmFields: 'Map topics to CRM fields (Optional)',
    crmFieldMapping: 'CRM Field Mapping',
    noCategoriesFound: 'No smart topics added',
    activateMethodology: 'Activate Methodology',
    saveChanges: 'Save Changes',
    assignMethodologyToPipeline:
      'Select a pipeline from your CRM to apply this methodology',
    icon: 'Icon',
    smartCategory: 'Smart Topic',
    segmentDescription: 'Topic Description',
    noInformation: 'No information detected',
    onboardingPending:
      'It may take up to 24 hours for the extracted insights to be reflected in the dashboard.',
    methodologyNotConfigured: 'Methodology not configured',
    selectPipelineToEnablePlaybook:
      'Assign a pipeline to activate this methodology. All other settings are pre-filled with recommended defaults — you can review or customize them anytime.',
    selectPipeline: 'Select a pipeline',
    dropdownTitle: 'Deal Pipeline',
    pipelineAlreadyUsedInPlaybook:
      'An AI Methodology has already been assigned to this pipeline',
    noMappedPlaybook: 'No methodology mapped to deal',
    dealDashboardOnboardingInProgress: (playbook: string) =>
      `Insights extraction is in progress for ${playbook}.`,
    legend: {
      noDiscussion: 'No discussions for this topic',
      containsDiscussion: 'One or more discussions for this topic'
    },
    onlyAdminsActivate: 'Only admins can activate a methodology',
    onlyAdminsDeactivate: 'Only admins can deactivate a methodology',
    onlyAdminsConfigurePipeline:
      'Only admins can configure pipeline assignments',
    notAvailableForCRM:
      'AI Methodology is currently only available for HubSpot, Salesforce, Zoho & Pipedrive CRMs',
    onboardingInProgress:
      'Editing has been disabled while insights for this methodology are being extracted. This process may take up to 24 hours to complete.',
    summarizedByAi: 'Summarized by Avoma AI',
    editSummary: 'Edit summary',
    viewInDrawer: 'View topic in drawer',
    scoreInsights: 'Score Insights',
    expandQuestionChoices: 'Expand question choices',
    collapseQuestionChoices: 'Collapse question choices',
    noScoreInsightsAvailable: 'No score insights available',
    topicDiscussedButNoScoreInsightsAvailable:
      'Topic discussed, but no score insights available',
    choiceTooltip: 'Answer choice out of total number of choices',
    notApplicable: 'Topic not discussed or identified',
    learnMoreDrawer: {
      title: 'Summarization of Smart Topics in the deals dashboard',
      description:
        'Enable summarization to get notes from various meetings and emails under each Smart Topic consolidated into a single summary.',
      whenDisabled: 'When summarization is disabled',
      whenEnabled: 'When summarization is enabled'
    },
    playbookNotEnabled: {
      withPipelines: (link: ReactNode, pipelineName: string) => (
        <React.Fragment>
          No AI methodology is currently mapped to this deal's pipeline. To
          start summarizing insights automatically, go to {link} to map an AI
          methodology to {pipelineName} pipeline.
        </React.Fragment>
      ),
      withoutPipelines: (link: ReactNode) => (
        <React.Fragment>
          No AI Methodologies are currently enabled for deals. To start
          summarizing insights automatically, go to {link} and enable one.
        </React.Fragment>
      )
    },
    topics: 'Topics',
    // Template and Scorecard selection text
    templateSelection: {
      description:
        "Select an AI Notes template to auto-capture this methodology's topics across different meetings.",
      placeholder: 'Select template',
      searchPlaceholder: 'Search templates...',
      editSelected: 'Edit selected template',
      useMethodologyOption: {
        title: 'Use methodology template',
        description:
          'Will be applied to only the meetings linked to deals in the selected pipeline'
      },
      useDefaultOption: {
        title: 'Keep using purpose based templates',
        description:
          'Will be applied based on meeting purpose, no link to deals or pipelines'
      }
    },
    scorecardSelection: {
      description:
        "Automatically evaluate calls based on this methodology's criteria",
      placeholder: 'Select scorecard',
      searchPlaceholder: 'Search scorecards...',
      editSelected: 'View scorecard questions',
      callScoringTemplate: 'Conversation scoring template'
    },
    aiGeneratedRules: {
      pipelineBased:
        "We've automatically created rules based on your pipeline selection",
      default: 'Automatically generated by AI'
    },
    scorecardRules: {
      changesSavedInfo: 'Changes will be saved to the scorecard template'
    },
    // Header section text
    header: {
      active: 'Active',
      inactive: 'Inactive',
      fullyConfigured: 'Fully configured',
      notConfigured: 'Not Configured',
      created: 'Created:',
      updated: 'Updated:',
      backToMethodologies: 'Back to AI Methodologies'
    },
    // Section titles for methodology setup
    sections: {
      pipelineAssignment: 'Pipeline Assignment',
      crmFieldAutomation: 'Smart Topic and CRM Mapping ',
      aiNotesAutomation: 'AI Notes Automation',
      aiCallScoring: 'AI Conversation Scoring',
      aiMethodologyScoring: 'AI Methodology Scoring',
      liveMethodologyMentor: 'Live Methodology Mentor'
    },
    // Card and listing text
    card: {
      assignedPipelines: 'Assigned Pipelines',
      noPipelinesAssigned: 'No pipelines assigned',
      viewConfiguration: 'View Configuration',
      topics: 'Topics',
      editSuccess: 'Playbook edited successfully',
      editError: 'Failed to edit playbook. Please try again.',
      duplicateSuccess: 'Playbook duplicated successfully',
      duplicateError: 'Failed to duplicate playbook. Please try again.',
      deleteSuccess: 'Playbook deleted successfully',
      deleteError: 'Failed to delete playbook. Please try again.'
    },
    // Field mapping notifications
    fieldMapping: {
      autoMatchInfo:
        'Avoma auto-matches CRM fields for you. Review and make necessary changes as appropriate.',
      allFieldsMapped:
        'All fields are mapped. You can review and modify the mappings below.',
      findMatches: 'Find Matches',
      findingMatches: 'Matching smart topics to CRM fields',
      analyzingFields:
        'Searching your CRM for the best field match for each extracted smart topic.',
      errorFindingMatches: 'Error finding matches',
      crmIntegrationRequired: 'CRM integration required',
      saveSuccess: 'Field mappings saved successfully',
      saveError: 'Failed to save field mappings. Please try again.',
      // Auto-matching result messages
      autoMatchingDone: 'Auto-matching done',
      autoMatchingSuccessMessage:
        "We've identified the best CRM field for each smart topic. Confirm or update the matches.",
      noExactMatchesFound: 'No exact matches found',
      noExactMatchesMessage:
        "We couldn't find CRM fields that closely match some smart topics. Please map them manually.",
      // Field mapping status messages
      alreadyMappedTo: (fieldName: string) => `Already mapped to ${fieldName}`,
      alreadyMappedToAnotherCategory: 'Already mapped to another category',
      deleteTopicTooltip: 'Remove topic',
      dragAndDropTooltip: 'Drag and drop to reorder smart topics'
    },
    // Action buttons
    configure: 'Configure',
    dealScoring: {
      saveSuccess: 'Methodology scoring changes saved successfully',
      saveError: 'Failed to save scorecard changes. Please try again.',
      aiGeneratedQuestionAndChoicesTooltip:
        'The question and scoring choices for this topic were generated by Avoma AI'
    },
    // Pipeline assignment confirmation dialogs
    pipelineAssignment: {
      removeAllTitle: 'Remove all pipelines?',
      removeAllText:
        'Removing all pipelines will deactivate this methodology. Are you sure you want to continue?',
      removeAllConfirmText: 'Remove Pipelines and Deactivate',
      updateTitle: 'Update Pipeline Assignment?',
      updateText:
        'Are you sure you want to update the pipeline assignment for this methodology?',
      updateConfirmText: 'Update Pipeline Assignment'
    },
    liveMethodologyMentor: {
      enable: 'Yes, enable',
      disable: 'Disable for all users in my organization'
    },
    analysis: 'Analysis',
    noCategories: 'Add at least one smart topic to this methodology to continue'
  },
  reports: {
    lowRiskDeals: 'Low Risk Deals',
    mediumRiskDeals: 'Medium Risk Deals',
    highRiskDeals: 'High Risk Deals'
  },
  contacts: {
    title: 'Contacts',
    objectsName: 'contacts'
  },
  leads: {
    title: 'Leads',
    objectsName: 'leads'
  },
  companies: {
    title: 'Companies',
    objectsName: 'companies'
  },
  columnChooserDrawer: {
    totalColumnsSelected: (count: number) => `${count} columns selected`
  },
  methodologyMentorTitle: 'Methodology Mentor',
  methodologyMentorCollapse: 'Collapse',
  methodologyMentorExpand: 'Expand'
};
